import React, { useEffect, useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { formatBranchMoney, staffApi } from "@/lib/api";

/**
 * Services tab: name filter + per-row specialist picker + qty +
 * "Add to order" button that is DISABLED until a Care Team member is
 * chosen for that row.
 *
 * Smart Specialist Filtering (Feb 2026 · Appointment-Date-Aware Iter-332):
 * the per-row specialist Select is populated from the SAME
 * `GET /branches/{bid}/staff?date=…` endpoint that powers the booking
 * calendar. That endpoint already applies:
 *   • Gate A — Training: only Care Team members whose `service_ids`
 *     include the row's service can appear.
 *   • Gate B — On-Duty: after every schedule layer (home template →
 *     per-branch regular schedule → weekly override) is merged for the
 *     given day, staff whose effective mode is Day Off / Training Day /
 *     Closed / Public Holiday / Annual Leave / Sick Leave are dropped.
 *   • Role Gate: Owners, Admins, Managers, Supervisors, Reservation and
 *     Store roles never appear (management `role_id` set is excluded
 *     server-side).
 *
 * `appointmentDate` (Asia/Makassar `YYYY-MM-DD`) drives the `date=` param
 * so a closed order raised weeks ago shows the Care Team that was on
 * duty on the TREATMENT DATE — not today. Falls back to the current
 * calendar day when the caller doesn't supply one (e.g. a fresh Walk-in
 * pending sale with no items or treatment_date yet).
 *
 * The mandatory-specialist rule is enforced both here (button
 * disabled) and on the backend (`POST /orders/{oid}/items` 400s a
 * service line without staff_ids). This keeps commission / payroll
 * / calendar mirroring correct — a service without a provider has
 * nowhere to record who actually delivered the work.
 *
 * Emits the standard service order-line payload via `onAdd` with the
 * selected specialist pre-attached under `staff_ids`.
 */
export default function ServicesPanel({ services, branch, staffList = [], appointmentDate = null, onAdd }) {
  const [serviceQ, setServiceQ] = useState("");
  const [qtyById, setQtyById] = useState({});
  const [staffByRow, setStaffByRow] = useState({});
  // On-duty + non-management staff for THIS branch on the APPOINTMENT
  // DATE (falls back to today when the caller doesn't specify one).
  // Falls back to the parent-supplied `staffList` if the calendar
  // endpoint errors so the dropdown never silently empties (a broken
  // fetch shouldn't prevent an in-person walk-in from being added).
  const [dutyStaff, setDutyStaff] = useState(staffList);

  useEffect(() => {
    if (!branch?.id) { setDutyStaff(staffList); return; }
    let cancelled = false;
    let iso = appointmentDate;
    if (!iso || typeof iso !== "string" || iso.length < 10) {
      const today = new Date();
      iso = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
    } else {
      iso = iso.slice(0, 10);
    }
    staffApi
      .get(`/branches/${branch.id}/staff`, { params: { date: iso } })
      .then((res) => { if (!cancelled) setDutyStaff(Array.isArray(res.data) ? res.data : staffList); })
      .catch(() => { if (!cancelled) setDutyStaff(staffList); });
    return () => { cancelled = true; };
  }, [branch?.id, staffList, appointmentDate]);

  const filteredServices = useMemo(() => {
    const q = serviceQ.trim().toLowerCase();
    return services.filter((sv) => q === "" || (sv.name || "").toLowerCase().includes(q));
  }, [services, serviceQ]);

  // Per-service eligible specialists = on-duty staff who are trained on
  // this service. Memoised over `dutyStaff` so switching rows / typing
  // in the filter box is instant.
  const eligibleByService = useMemo(() => {
    const map = {};
    for (const sv of filteredServices) {
      map[sv.id] = dutyStaff.filter((s) => (s.service_ids || []).includes(sv.id));
    }
    return map;
  }, [filteredServices, dutyStaff]);

  const getQty = (id) => qtyById[id] || 1;
  const getStaff = (id) => staffByRow[id] || "";

  const handleAdd = (row) => {
    const staffId = getStaff(row.id);
    if (!staffId) return;
    const qty = getQty(row.id);
    onAdd({
      kind: "service",
      ref_id: row.id,
      name: row.name,
      quantity: qty,
      unit_price: row.price || 0,
      discount: 0,
      staff_ids: [staffId],
    });
    // Reset the row's specialist so the operator doesn't accidentally
    // add the same service to two lines under one Care Team member.
    setStaffByRow((prev) => ({ ...prev, [row.id]: "" }));
  };

  return (
    <div className="space-y-3">
      <Input
        placeholder="Filter services…"
        value={serviceQ}
        onChange={(e) => setServiceQ(e.target.value)}
        data-testid="add-service-name"
      />
      <div className="border border-border rounded-lg max-h-[420px] overflow-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Service</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Duration</TableHead>
              <TableHead>Specialist *</TableHead>
              <TableHead className="text-right">Price</TableHead>
              <TableHead className="w-24">Qty</TableHead>
              <TableHead className="w-32 text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredServices.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-sm text-muted-foreground py-8">
                  No services match.
                </TableCell>
              </TableRow>
            ) : filteredServices.map((r) => {
              const staffId = getStaff(r.id);
              const canAdd = !!staffId;
              const rowStaff = eligibleByService[r.id] || [];
              return (
                <TableRow key={r.id} data-testid={`add-service-row-${r.id}`}>
                  <TableCell><div className="font-medium text-sm">{r.name}</div></TableCell>
                  <TableCell><span className="text-xs text-muted-foreground">{r.category || "-"}</span></TableCell>
                  <TableCell><span className="text-xs text-muted-foreground">{r.duration_min || 60} min</span></TableCell>
                  <TableCell>
                    <Select
                      value={staffId}
                      onValueChange={(v) => setStaffByRow((prev) => ({ ...prev, [r.id]: v }))}
                    >
                      <SelectTrigger className="h-8 w-48" data-testid={`add-service-staff-${r.id}`}>
                        <SelectValue placeholder={rowStaff.length ? "Choose specialist…" : "None on duty"} />
                      </SelectTrigger>
                      <SelectContent>
                        {rowStaff.length === 0 ? (
                          <div
                            className="px-2 py-2 text-xs text-muted-foreground"
                            data-testid={`add-service-staff-empty-${r.id}`}
                          >
              No trained specialist on duty on this treatment date.
                          </div>
                        ) : rowStaff.map((s) => (
                          <SelectItem key={s.id} value={s.id} data-testid={`add-service-staff-option-${r.id}-${s.id}`}>
                            {s.full_name || s.name || s.email}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="text-right">
                    {r.price == null
                      ? <span className="text-xs uppercase tracking-wider text-muted-foreground">Custom</span>
                      : formatBranchMoney(branch, r.price || 0)}
                  </TableCell>
                  <TableCell>
                    <Input
                      type="number"
                      min={1}
                      value={getQty(r.id)}
                      onChange={(e) => setQtyById((q) => ({ ...q, [r.id]: Math.max(1, parseInt(e.target.value || "1", 10)) }))}
                      className="w-20"
                      data-testid={`add-service-qty-${r.id}`}
                    />
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-primary hover:text-primary hover:bg-primary/10 uppercase tracking-wider text-[11px] disabled:opacity-40 disabled:cursor-not-allowed"
                      onClick={() => handleAdd(r)}
                      disabled={!canAdd}
                      title={canAdd ? "Add to order" : "Pick a specialist first"}
                      data-testid={`add-service-add-${r.id}`}
                    >
                      Add to order
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      <p className="text-[11px] text-muted-foreground">
        * A Care Team member is required on every service before it can be added — needed for payroll and commission attribution.
      </p>
    </div>
  );
}
